import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';
import ConfiguracionSecuenciaModal from './ConfiguracionSecuenciaModal';



// ====================================================================
// 1. Funciones de Ayuda (Definiciones de formato)
// ====================================================================

const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '$0.00';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num || 0);
};

const formatDate = (dateString) => {
    try {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return date.toLocaleDateString('es-MX', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    } catch (e) {
        return dateString;
    }
};

// Constantes para áreas táctiles (mínimo 44x44px)
const TOUCH_MIN_SIZE = '44px';

const PuntosVenta = () => {
    const navigate = useNavigate();
    const location = useLocation();
    // Modo "editar orden existente" (llega desde Órdenes > Editar)
    const [editOrder, setEditOrder] = useState(location.state?.editOrder || null);
    const [loadingEditOrder, setLoadingEditOrder] = useState(!!location.state?.editOrder);
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isSuperAdmin = roleName === 'SUPER_ADMIN' || user?.is_superuser;
    const isAdminFastFood = roleName === 'ADMIN_FAST_FOOD';
    const isCook = roleName === 'COOK' || user?.role === 'COOK' || user?.role_details?.description?.toLowerCase()?.includes('cocinero');
    const isAdmin = isSuperAdmin || isAdminFastFood || user?.role === 'admin';
    const canAccessFeria = isAdmin || isCook;

    // =====================================
    // 1. ESTADO DE DATOS Y CARGA
    // =====================================
    const [currentShift, setCurrentShift] = useState(null);
    const [currentLocalShift, setCurrentLocalShift] = useState(null);
    const [currentFeriaShift, setCurrentFeriaShift] = useState(null);
    const [checkingShift, setCheckingShift] = useState(true);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [promoCategories, setPromoCategories] = useState([]);
    const [selectedPromoCategory, setSelectedPromoCategory] = useState('all');
    const [tables, setTables] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [processingOrder, setProcessingOrder] = useState(false);
    const [printStep, setPrintStep] = useState(''); // animación de carga
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [showOrderDetails, setShowOrderDetails] = useState(false);
    const [editingNoteForItem, setEditingNoteForItem] = useState(null);
    const [noteText, setNoteText] = useState('');

    // 2. ESTADO DEL PUNTO DE VENTA
    const [cart, setCart] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTable, setSelectedTable] = useState('');
    const [discountCode, setDiscountCode] = useState('');
    const [orderDiscountPercentage, setOrderDiscountPercentage] = useState(0);
    const [isEmployeeDiscount, setIsEmployeeDiscount] = useState(false);
    const [appliedDiscount, setAppliedDiscount] = useState(null);

    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [transferReference, setTransferReference] = useState('');
    const [documentType, setDocumentType] = useState('factura'); // 'factura' = SRI KoaDoc, 'nota_entrega' = Local

    // Pagos Divididos (Mixtos)
    const [split1Method, setSplit1Method] = useState('efectivo');
    const [split1Currency, setSplit1Currency] = useState('USD');
    const [split1Amount, setSplit1Amount] = useState('');
    
    const [split2Method, setSplit2Method] = useState('transferencia');
    const [split2Currency, setSplit2Currency] = useState('COP');
    const [split2Amount, setSplit2Amount] = useState('');

    const [showReviewModal, setShowReviewModal] = useState(false);
    const [showSequenceModal, setShowSequenceModal] = useState(false);


    // 3.5 ESTADO DE CALCULADORA DE VUELTO
    const [cashGiven, setCashGiven] = useState(null);
    const [inputCash, setInputCash] = useState('');
    const [currency, setCurrency] = useState('USD');
    const [exchangeRate, setExchangeRate] = useState(parseFloat(localStorage.getItem('usdExchangeRate')) || 4000);

    // 3. ESTADO DE CLIENTES
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearch, setCustomerSearch] = useState('');

    // 4. ESTADO PARA TOPPINGS
    const [availableToppings, setAvailableToppings] = useState([]);
    const [showToppingsModal, setShowToppingsModal] = useState(false);
    const [activeToppingsItemIndex, setActiveToppingsItemIndex] = useState(null);

    // 5. ESTADO PARA VARIABLES
    const [showVariablesModal, setShowVariablesModal] = useState(false);
    const [selectedProductForVariables, setSelectedProductForVariables] = useState(null);
    const [currentVariablesSelection, setCurrentVariablesSelection] = useState({});

    // 6. ESTADO PARA ÓRDENES TEMPORALES / EN ESPERA (PARKED ORDERS)
    const [heldOrders, setHeldOrders] = useState(() => {
        try {
            const saved = localStorage.getItem('saiosushi_held_orders');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });
    const [showHeldOrdersModal, setShowHeldOrdersModal] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem('saiosushi_held_orders', JSON.stringify(heldOrders));
        } catch (e) {
            console.error('Error saving held orders:', e);
        }
    }, [heldOrders]);

    const handleHoldCurrentOrder = () => {
        if (cart.length === 0) {
            alert('El carrito está vacío. Agrega productos antes de poner la orden en espera.');
            return;
        }

        const orderSnapshot = {
            id: 'HOLD-' + Date.now().toString(36).toUpperCase(),
            held_at: new Date().toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' }),
            cart: [...cart],
            selectedCustomer: selectedCustomer ? { ...selectedCustomer } : null,
            customerSearch: customerSearch,
            selectedTable: selectedTable,
            orderDiscountPercentage: orderDiscountPercentage,
            appliedDiscount: appliedDiscount,
            isEmployeeDiscount: isEmployeeDiscount,
            discountCode: discountCode,
            paymentMethod: paymentMethod,
            currency: currency,
            documentType: documentType,
            total: calculateTotal
        };

        setHeldOrders(prev => [orderSnapshot, ...prev]);

        // Limpiar la pantalla actual para atender al siguiente cliente
        setCart([]);
        setSelectedCustomer(null);
        setCustomerSearch('');
        setSelectedTable('');
        setOrderDiscountPercentage(0);
        setAppliedDiscount(null);
        setIsEmployeeDiscount(false);
        setDiscountCode('');
        setCashGiven(null);
        setInputCash('');
        setShowOrderDetails(false);

        alert(`Orden #${orderSnapshot.id} puesta en espera.\nPuedes continuar atendiendo a otros clientes.`);
    };

    const handleRestoreHeldOrder = (heldOrder) => {
        if (cart.length > 0) {
            const confirmOverwrite = window.confirm('Ya tienes productos en el carrito actual. ¿Deseas reemplazar el carrito con la orden en espera?');
            if (!confirmOverwrite) return;
        }

        setCart(heldOrder.cart || []);
        setSelectedCustomer(heldOrder.selectedCustomer || null);
        setCustomerSearch(heldOrder.customerSearch || '');
        setSelectedTable(heldOrder.selectedTable || '');
        setOrderDiscountPercentage(heldOrder.orderDiscountPercentage || 0);
        setAppliedDiscount(heldOrder.appliedDiscount || null);
        setIsEmployeeDiscount(heldOrder.isEmployeeDiscount || false);
        setDiscountCode(heldOrder.discountCode || '');
        setPaymentMethod(heldOrder.paymentMethod || 'efectivo');
        setCurrency(heldOrder.currency || 'USD');
        setDocumentType(heldOrder.documentType || 'factura');

        // Remover de la lista de órdenes en espera
        setHeldOrders(prev => prev.filter(o => o.id !== heldOrder.id));
        setShowHeldOrdersModal(false);
    };

    const handleDeleteHeldOrder = (heldId, e) => {
        if (e) e.stopPropagation();
        if (window.confirm('¿Estás seguro de eliminar esta orden en espera?')) {
            setHeldOrders(prev => prev.filter(o => o.id !== heldId));
        }
    };

    // =====================================
    // 4. EFECTOS - CARGA INICIAL DE DATOS Y RESPONSIVIDAD
    // =====================================
    // Inject custom scrollbar styles
    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            ::-webkit-scrollbar {
                width: 8px;
                height: 8px;
            }
            ::-webkit-scrollbar-track {
                background: #f4efe6; 
            }
            ::-webkit-scrollbar-thumb {
                background: var(--border-color); 
                border-radius: 4px;
            }
            ::-webkit-scrollbar-thumb:hover {
                background: var(--border-color); 
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    useEffect(() => {
        let isMounted = true;

        const fetchData = async () => {
            try {
                const [localRes, feriaRes] = await Promise.allSettled([
                    api.get('/api/pos/shifts/current/', {
                        baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                        params: { is_feria: false }
                    }),
                    api.get('/api/pos/shifts/current/', {
                        baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE,
                        params: { is_feria: true }
                    })
                ]);
                
                if (isMounted) {
                    const extractOpenShift = (res) => {
                        if (res.status !== 'fulfilled' || !res.value?.data) return null;
                        const data = res.value.data;
                        if (data.shift && data.shift.id && data.shift.status === 'open') return data.shift;
                        if (data.id && data.status === 'open') return data;
                        return null;
                    };

                    const localData = extractOpenShift(localRes);
                    const feriaData = extractOpenShift(feriaRes);
                    
                    setCurrentLocalShift(localData);
                    setCurrentFeriaShift(feriaData);
                    
                    // El POS se habilita si la caja normal está abierta (para cualquier usuario)
                    // o si la caja de ferias está abierta (exclusivamente para usuarios con acceso a ferias: Admin / Cocinero)
                    const activeShiftForUser = localData || (canAccessFeria ? feriaData : null);
                    setCurrentShift(activeShiftForUser);
                }
            } catch (err) {
                console.warn('Error fetching shifts:', err);
            } finally {
                if (isMounted) {
                    setCheckingShift(false);
                }
            }

            try {
                const productsRes = await api.get('/api/menu/products/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;

                const loadedProducts = productsRes.data.results || productsRes.data || [];
                loadedProducts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setProducts(loadedProducts);

            } catch (err) {
                console.error('Error cargando productos:', err);
            }

            try {
                const categoriesRes = await api.get('/api/menu/categories/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;

                const loadedCategories = categoriesRes.data.results || categoriesRes.data || [];
                loadedCategories.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setCategories(loadedCategories);

            } catch (err) {
                console.error('Error cargando categorías:', err);
            }

            try {
                const promosRes = await api.get('/api/menu/combos/?is_promotion=true', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;

                const loadedPromos = promosRes.data.results || promosRes.data || [];
                setPromotions(loadedPromos.filter(p => p.is_active));

            } catch (err) {
                console.error('Error cargando promociones:', err);
            }

            try {
                const promoCatsRes = await api.get('/api/menu/promotion-categories/?is_active=true', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                if (isMounted) {
                    setPromoCategories(promoCatsRes.data.results || promoCatsRes.data || []);
                }
            } catch (err) {
                console.warn('Categorías de promociones no disponibles', err);
            }

            try {
                const tablesRes = await api.get('/api/pos/tables/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });

                if (!isMounted) return;
                setTables(tablesRes.data.results || tablesRes.data || []);

            } catch (err) {
                console.warn('Mesas no disponibles');
                if (isMounted) {
                    setTables([]);
                }
            }

            try {
                const toppingsRes = await api.get('/api/menu/toppings/', {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                if (isMounted) {
                    const data = toppingsRes.data.results || toppingsRes.data;
                    const arr = Array.isArray(data) ? data : [];
                    setAvailableToppings(arr.filter(t => t.is_active));
                }
            } catch (err) {
                console.warn('Toppings no disponibles', err);
            }

            if (isMounted) {
                setLoading(false);
            }
        };

        fetchData();

        return () => {
            isMounted = false;
        };
    }, [canAccessFeria]);

    // Modo edición: cargar los productos actuales de la orden en el carrito
    useEffect(() => {
        if (!editOrder) return;
        let isMounted = true;
        const loadOrderForEdit = async () => {
            try {
                const res = await api.get(`/api/orders/orders/${editOrder.order_number}/`, {
                    baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
                });
                if (!isMounted) return;
                const order = res.data;
                if (order.payment_status === 'paid' || order.status === 'cancelled' || order.sri_id) {
                    alert('Esta orden ya no se puede editar (está pagada, anulada o facturada).');
                    setEditOrder(null);
                    navigate('/fast-food/orders');
                    return;
                }
                setCart((order.items || []).map(it => ({
                    cartItemId: `existing-${it.id}`,
                    existing_item_id: it.id,
                    product_id: it.product,
                    name: it.product_details?.name || 'Producto',
                    description: it.product_details?.description || '',
                    price: parseFloat(it.unit_price) || 0,
                    quantity: it.quantity,
                    image: it.product_details?.image,
                    // Las notas guardadas ya incluyen toppings y variables elegidos al crear la orden
                    note: it.notes || '',
                    discount_percentage: parseFloat(it.discount_percentage) || 0,
                    isPromo: (it.notes || '').includes('Promo aplicada'),
                    max_toppings: 0,
                    selectedToppings: [],
                    selectedVariables: []
                })));
            } catch (err) {
                console.error('Error cargando la orden para editar:', err);
                alert('No se pudo cargar la orden para editarla.');
                if (isMounted) {
                    setEditOrder(null);
                    navigate('/fast-food/orders');
                }
            } finally {
                if (isMounted) setLoadingEditOrder(false);
            }
        };
        loadOrderForEdit();
        return () => { isMounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editOrder?.order_number]);

    // Detectar tamaño de pantalla
    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        handleResize(); // Ejecutar al inicio
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    // =====================================
    // 5. LÓGICA DEL CARRITO
    // =====================================
    const handleProductClick = (product, isPromo = false, promoQuantity = 1, promoDiscountPct = 0, promoName = '') => {
        if (!isPromo && product.variables && product.variables.length > 0) {
            setSelectedProductForVariables({ product, isPromo, promoQuantity, promoDiscountPct, promoName });
            const initialSelection = {};
            product.variables.forEach(v => {
                // Seleccionar por defecto la primera opción si la hay, sino null
                const defaultOption = v.options && v.options.length > 0 && v.options[0].is_active ? v.options[0] : null;
                initialSelection[v.id] = defaultOption;
            });
            setCurrentVariablesSelection(initialSelection);
            setShowVariablesModal(true);
        } else {
            addToCart(product, isPromo, promoQuantity, promoDiscountPct, promoName);
        }
    };

    const confirmVariablesSelection = () => {
        const { product, isPromo, promoQuantity, promoDiscountPct, promoName } = selectedProductForVariables;
        
        // Validar que todas las variables estén seleccionadas
        const missingVariables = product.variables.filter(v => !currentVariablesSelection[v.id]);
        if (missingVariables.length > 0) {
            alert(`Por favor selecciona una opción para: ${missingVariables.map(v => v.name).join(', ')}`);
            return;
        }

        const selectedVariables = product.variables.map(v => ({
            variable_id: v.id,
            variable: v.name,
            option_id: currentVariablesSelection[v.id].id,
            option: currentVariablesSelection[v.id].name,
            extra_price: parseFloat(currentVariablesSelection[v.id].extra_price) || 0
        }));

        addToCart(product, isPromo, promoQuantity, promoDiscountPct, promoName, selectedVariables);
        setShowVariablesModal(false);
        setSelectedProductForVariables(null);
        setCurrentVariablesSelection({});
    };

    const addToCart = useCallback((product, isPromo = false, promoQuantity = 1, promoDiscountPct = 0, promoName = '', selectedVariables = []) => {
        setCart(prevCart => {
            const variablesPriceAdjustment = selectedVariables.reduce((total, v) => total + v.extra_price, 0);
            const existingItemIndex = prevCart.findIndex(item => 
                item.product_id === product.id && 
                item.isPromo === isPromo &&
                item.name === (isPromo ? promoName : product.name) &&
                !(product.max_toppings > 0) &&
                !(selectedVariables && selectedVariables.length > 0) &&
                !(item.selectedVariables && item.selectedVariables.length > 0)
            );
            if (existingItemIndex >= 0) {
                const newCart = [...prevCart];
                newCart[existingItemIndex] = {
                    ...newCart[existingItemIndex],
                    quantity: newCart[existingItemIndex].quantity + (isPromo ? promoQuantity : 1)
                };
                return newCart;
            } else {
                return [...prevCart, {
                    cartItemId: Date.now() + '-' + Math.random().toString(36).substring(7),
                    product_id: product.id,
                    name: isPromo ? promoName : product.name,
                    description: product.description || '',
                    price: parseFloat(product.price) + variablesPriceAdjustment,
                    quantity: isPromo ? promoQuantity : 1,
                    image: isPromo ? product.promoImage : product.image,
                    note: isPromo ? 'Promo aplicada' : '',
                    discount_percentage: isPromo ? promoDiscountPct : 0,
                    isPromo: isPromo,
                    max_toppings: isPromo ? 0 : (product.max_toppings || 0),
                    selectedToppings: [],
                    selectedVariables: selectedVariables
                }];
            }
        });
    }, []);

    const handleAddPromo = async (promo) => {
        // Promoción independiente: se vende con su propio nombre y precio mediante su producto interno
        if (promo.products_count === 0 && promo.standalone_product) {
            addToCart({
                id: promo.standalone_product,
                price: promo.price,
                description: promo.description,
                promoImage: promo.image
            }, true, 1, 0, promo.name);
            return;
        }
        try {
            const res = await api.get(`/api/menu/combos/${promo.id}/products/`, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            const promoProducts = res.data;
            if (promoProducts.length > 0) {
                const comboProduct = promoProducts[0];
                const realProduct = comboProduct.product;
                const quantity = comboProduct.quantity;
                
                const originalTotal = realProduct.price * quantity;
                let discountPct = 0;
                if (originalTotal > 0 && promo.price < originalTotal) {
                    discountPct = ((originalTotal - promo.price) / originalTotal) * 100;
                }
                
                // Agregamos imagen de la promo al producto temporalmente
                realProduct.promoImage = promo.image;
                
                addToCart(realProduct, true, quantity, discountPct, promo.name);
            } else {
                alert("Esta promoción no tiene productos asociados.");
            }
        } catch(e) {
            console.error("Error al cargar la promo", e);
        }
    };

    const removeFromCart = useCallback((itemToRemove) => {
        setCart(prevCart => prevCart.filter(item => 
            item.cartItemId !== itemToRemove.cartItemId
        ));
    }, []);

    const updateQuantity = useCallback((itemToUpdate, delta) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === itemToUpdate.cartItemId) {
                    const newQuantity = Math.max(1, item.quantity + delta);
                    return { ...item, quantity: newQuantity };
                }
                return item;
            });
        });
    }, []);

    const updateItemDiscount = useCallback((itemToUpdate, percentage) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === itemToUpdate.cartItemId) {
                    return { ...item, discount_percentage: Math.max(0, Math.min(100, percentage)) };
                }
                return item;
            });
        });
    }, []);

    const handleAddNote = (itemToNote) => {
        const item = cart.find(i => i.cartItemId === itemToNote.cartItemId);
        setEditingNoteForItem(itemToNote.cartItemId);
        setNoteText(item?.note || '');
    };

    const saveNote = () => {
        if (!editingNoteForItem) return;

        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.cartItemId === editingNoteForItem) {
                    return { ...item, note: noteText.trim() };
                }
                return item;
            });
        });

        setEditingNoteForItem(null);
        setNoteText('');
    };

    const cancelNote = () => {
        setEditingNoteForItem(null);
        setNoteText('');
    };

    // =====================================
    // 6. CÁLCULOS DE PRECIOS
    // =====================================
    const calculateSubtotal = useMemo(() => {
        return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
    }, [cart]);

    const calculateItemDiscounts = useMemo(() => {
        return cart.reduce((total, item) => {
            const percentage = parseFloat(item.discount_percentage) || 0;
            return total + (item.price * (percentage / 100) * item.quantity);
        }, 0);
    }, [cart]);

    const calculateOrderDiscount = useMemo(() => {
        const subtotalAfterItemDiscounts = calculateSubtotal - calculateItemDiscounts;
        const percentage = parseFloat(orderDiscountPercentage) || 0;
        return subtotalAfterItemDiscounts * (percentage / 100);
    }, [calculateSubtotal, calculateItemDiscounts, orderDiscountPercentage]);

    const calculateDiscountAmount = useMemo(() => {
        // También mantenemos appliedDiscount por si acaso
        let oldDiscount = 0;
        if (appliedDiscount) {
            const subtotal = calculateSubtotal - calculateItemDiscounts;
            if (appliedDiscount.discount_type === 'percentage') {
                oldDiscount = subtotal * (parseFloat(appliedDiscount.discount_value) / 100);
            } else if (appliedDiscount.discount_type === 'fixed_amount') {
                oldDiscount = Math.min(parseFloat(appliedDiscount.discount_value), subtotal);
            }
        }
        return calculateItemDiscounts + calculateOrderDiscount + oldDiscount;
    }, [calculateItemDiscounts, calculateOrderDiscount, appliedDiscount, calculateSubtotal]);

    const calculateTotal = useMemo(() => {
        const subtotal = calculateSubtotal;
        const discount = calculateDiscountAmount;
        return (subtotal - discount);
    }, [calculateSubtotal, calculateDiscountAmount]);

    // =====================================
    // 7. LÓGICA DE DESCUENTOS
    // =====================================
    const handleApplyDiscount = async () => {
        if (!discountCode) return;
        try {
            const response = await api.post('/api/pos/discounts/validate/', { code: discountCode }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            if (response.data.valid) {
                setAppliedDiscount(response.data.discount);
                alert('Descuento aplicado correctamente');
            } else {
                alert(response.data.message || 'Código inválido');
                setAppliedDiscount(null);
            }
        } catch (err) {
            console.error('Error validating discount:', err);
            alert('Error al validar descuento');
            setAppliedDiscount(null);
        }
    };

    // =====================================
    // 8. LÓGICA DE CLIENTES
    // =====================================
    // =====================================
    // 9. LÓGICA DE PROCESAMIENTO PRINCIPAL
    // =====================================

    const filteredProducts = useMemo(() => {
        return products.filter(product => {
            const matchesCategory = selectedCategory === 'all' || product.category === selectedCategory;
            const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
            const isAvailable = product.is_active && product.is_available;

            return matchesCategory && matchesSearch && isAvailable;
        });
    }, [products, selectedCategory, searchTerm]);

    // Subcategorías de promociones que tienen al menos una promoción activa
    const visiblePromoCategories = useMemo(() => {
        return promoCategories.filter(cat => promotions.some(p => p.promotion_category === cat.id));
    }, [promoCategories, promotions]);

    const filteredPromotions = useMemo(() => {
        return promotions.filter(promo => {
            const matchesCategory = selectedPromoCategory === 'all' || promo.promotion_category === selectedPromoCategory;
            const matchesSearch = promo.name.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesCategory && matchesSearch;
        });
    }, [promotions, selectedPromoCategory, searchTerm]);

    const renderPromoSubcategories = () => {
        if (selectedCategory !== 'promotions' || visiblePromoCategories.length === 0) return null;
        return (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
                {[{ id: 'all', name: 'Todas', color: '#c9973a' }, ...visiblePromoCategories].map(cat => {
                    const isSelected = selectedPromoCategory === cat.id;
                    const accent = cat.color || '#c9973a';
                    return (
                        <button
                            key={cat.id}
                            onClick={() => setSelectedPromoCategory(cat.id)}
                            style={{
                                padding: '0.4rem 0.9rem',
                                borderRadius: '999px',
                                border: `2px solid ${accent}`,
                                backgroundColor: isSelected ? accent : '#ffffff',
                                color: isSelected ? '#ffffff' : '#4a3f36',
                                fontWeight: '600',
                                fontSize: '0.85rem',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                minHeight: '36px'
                            }}
                        >
                            {cat.name}
                        </button>
                    );
                })}
            </div>
        );
    };

    // Validation for order placement
    const isPaymentValid = useMemo(() => {
        return cart.length > 0;
    }, [cart.length]);

    // Items del carrito en el formato del backend (crear orden / agregar a orden)
    const buildOrderItemsPayload = () => cart.map(item => {
        let finalNote = item.note || '';
        if (item.selectedToppings && item.selectedToppings.length > 0) {
            const toppingsText = `Toppings: ${item.selectedToppings.join(', ')}`;
            finalNote = finalNote ? `${finalNote} | ${toppingsText}` : toppingsText;
        }
        return {
            ...(item.existing_item_id ? { id: item.existing_item_id } : {}),
            product_id: item.product_id,
            quantity: item.quantity,
            notes: finalNote,
            discount_percentage: Number((parseFloat(item.discount_percentage) || 0).toFixed(2)),
            selected_variables: item.selectedVariables || []
        };
    });

    // Items del carrito en el formato del ticket impreso
    const buildReceiptItems = () => cart.map(item => {
        let receiptNote = item.note || '';
        if (item.selectedToppings && item.selectedToppings.length > 0) {
            const toppingsText = `Toppings: ${item.selectedToppings.join(', ')}`;
            receiptNote = receiptNote ? `${receiptNote} | ${toppingsText}` : toppingsText;
        }
        if (item.selectedVariables && item.selectedVariables.length > 0) {
            const variablesText = item.selectedVariables.map(v => `${v.variable}: ${v.option}`).join(', ');
            receiptNote = receiptNote ? `${receiptNote} | ${variablesText}` : variablesText;
        }
        return {
            name: item.name,
            description: item.description || '',
            quantity: item.quantity,
            price: parseFloat(item.price),
            discount_percentage: Number((parseFloat(item.discount_percentage) || 0).toFixed(2)),
            total: parseFloat((item.price * item.quantity * (1 - (parseFloat(item.discount_percentage) || 0) / 100)).toFixed(2)),
            note: receiptNote
        };
    });

    const cancelEditOrder = () => {
        setEditOrder(null);
        setCart([]);
        navigate('/fast-food/pos', { replace: true, state: null });
    };

    // Guarda los cambios del carrito sobre la orden existente (llegando desde Órdenes > Editar)
    const submitOrderEdit = async () => {
        if (!editOrder) return;
        if (cart.length === 0) {
            alert('La orden debe tener al menos un producto. Para quitar todo, anule la orden desde Órdenes.');
            return;
        }
        setProcessingOrder(true);
        setShowReviewModal(false);
        try {
            const res = await api.post(
                `/api/orders/orders/${editOrder.order_number}/edit_items/`,
                { items: buildOrderItemsPayload() },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );
            const updatedOrder = res.data;
            const changes = updatedOrder.changes || [];

            // Ticket solo con los cambios, para que cocina sepa qué agregar o quitar
            if (changes.length > 0) {
                setPrintStep('Enviando cambios a impresora...');
                try {
                    const total = parseFloat(updatedOrder.total) || 0;
                    await printerService.printReceipt({
                        order_number: updatedOrder.order_number,
                        order_sequence: updatedOrder.order_sequence,
                        customer_name: 'ORDEN MODIFICADA',
                        customer_dni: '9999999999999',
                        cashier_name: currentShift?.user_name || 'CAJA',
                        table_number: `${editOrder.table_label || ''} (MODIFICADA)`.trim(),
                        items: changes.map(c => ({
                            name: c.quantity > 0 ? `AGREGAR: ${c.name}` : `QUITAR: ${c.name}`,
                            description: '',
                            quantity: Math.abs(c.quantity),
                            price: 0,
                            discount_percentage: 0,
                            total: 0,
                            note: c.notes || ''
                        })),
                        subtotal: Number((total / 1.15).toFixed(2)),
                        discount: 0,
                        tax: Number((total - total / 1.15).toFixed(2)),
                        total: Number(total.toFixed(2)),
                        payment_method: 'pendiente',
                        payment_reference: '',
                        document_type: 'nota_entrega',
                        printed_at: new Date().toISOString()
                    });
                } catch (printError) {
                    console.error('Error al imprimir cambios:', printError);
                    alert('La orden se actualizó, pero no se pudo imprimir el ticket de cambios.');
                } finally {
                    setPrintStep('');
                }
            }

            alert(
                `Orden${updatedOrder.order_sequence ? ` #${updatedOrder.order_sequence}` : ''} actualizada.\n` +
                `Nuevo total: $${parseFloat(updatedOrder.total || 0).toFixed(2)}`
            );
            setCart([]);
            setEditOrder(null);
            navigate('/fast-food/orders');
        } catch (err) {
            console.error('Error al editar la orden:', err);
            const data = err.response?.data;
            const detail = data?.non_field_errors?.[0] || data?.items?.[0] || data?.detail || (data ? JSON.stringify(data) : err.message);
            alert(`No se pudo actualizar la orden: ${typeof detail === 'string' ? detail : JSON.stringify(detail)}`);
        } finally {
            setProcessingOrder(false);
        }
    };

    // El comprobante (Factura SRI) se emite desde Órdenes al cobrar; aquí solo se guarda la orden
    const handleInitiatePlaceOrder = (isFeria = false) => {
        if (cart.length === 0) return;
        if (editOrder) {
            submitOrderEdit();
            return;
        }
        finalPlaceOrder(isFeria, 'nota_entrega');
    };

    // FUNCIÓN PRINCIPAL CON IMPRESIÓN
    const finalPlaceOrder = async (isFeria = false, chosenDocType = null) => {
        if (cart.length === 0) return;

        const activeDocType = chosenDocType || documentType || 'factura';

        setProcessingOrder(true);
        setShowReviewModal(false);

        let orderType = 'dine_in';
        let tableNumber = selectedTable;
        const DEFAULT_TABLE_NAME = 'GENERICA';

        if (selectedTable === 'takeout') {
            orderType = 'takeout';
            tableNumber = '';
        } else if (!selectedTable || selectedTable === 'Seleccionar mesa...') {
            orderType = 'dine_in';
            tableNumber = DEFAULT_TABLE_NAME;
        } else {
            orderType = 'dine_in';
            tableNumber = selectedTable;
        }

        // Preparar notas generales
        let orderNotes = '';
        if (isEmployeeDiscount) {
            orderNotes += '[VENTA_EMPLEADO]';
        }

        // Modificado para incluir notas en los items y porcentajes de descuento
        const orderPayload = {
            order_type: orderType,
            table_number: tableNumber,
            notes: orderNotes,
            discount_percentage: Number((parseFloat(orderDiscountPercentage) || 0).toFixed(2)),
            payment_method: 'efectivo',
            payment_reference: '',
            cash_amount: 0,
            transfer_amount: 0,
            items: buildOrderItemsPayload(),
            discount_code: appliedDiscount ? appliedDiscount.code : null,
            customer_id: selectedCustomer ? selectedCustomer.id : null,
            skip_print: true, // Evitar que el backend imprima doble
            is_feria: isFeria
        };

        try {
            // 1. CREAR LA ORDEN
            const orderResponse = await api.post('/api/orders/orders/', orderPayload, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            const createdOrder = orderResponse.data;

            // 2. PREPARAR DATOS PARA EL TICKET (incluyendo notas y correlativo de orden)
            const receiptData = {
                order_number: createdOrder.order_number || createdOrder.id,
                order_sequence: createdOrder.order_sequence,
                customer_name: selectedCustomer
                    ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`.trim()
                    : 'CONSUMIDOR FINAL',
                customer_dni: selectedCustomer ? (selectedCustomer.cedula || selectedCustomer.dni || selectedCustomer.ruc) : '9999999999999',
                cashier_name: currentShift?.user_name || 'CAJA',
                table_number: selectedTable === 'takeout' ? 'PARA LLEVAR' : (selectedTable || 'MESA GENÉRICA'),
                items: buildReceiptItems(),
                subtotal: Number(parseFloat(calculateTotal / 1.15).toFixed(2)),
                discount: Number(parseFloat(calculateDiscountAmount).toFixed(2)),
                tax: Number(parseFloat(calculateTotal - (calculateTotal / 1.15)).toFixed(2)), // IVA 15% desglosado
                total: Number(parseFloat(calculateTotal).toFixed(2)),
                payment_method: 'pendiente',
                payment_reference: '',
                document_type: activeDocType, // 'factura' o 'nota_entrega'
                printed_at: new Date().toISOString() // Hora del cliente para el ticket
            };

            // 3. EMISIÓN DE FACTURA ELECTRÓNICA SRI + espera de clave de acceso
            if (createdOrder?.id && activeDocType === 'factura') {
                try {
                    setPrintStep('Emitiendo factura electrónica...');
                    const billingRes = await billingService.emitOrderInvoice(createdOrder.id);
                    console.log('Respuesta SRI KoaDoc:', billingRes);
                    const invoiceObj = billingRes?.invoice || billingRes?.data?.invoice || billingRes?.data || billingRes;
                    if (invoiceObj) {
                        receiptData.sri_access_key = invoiceObj.access_key || invoiceObj.sri_access_key || invoiceObj.clave_acceso;
                        receiptData.sri_document_number = invoiceObj.number || invoiceObj.document_number || invoiceObj.sri_document_number || invoiceObj.numero_documento;
                    }

                    // Si aún no tenemos la clave, esperar hasta 5 seg consultando la BD
                    if (!receiptData.sri_access_key) {
                        setPrintStep('Esperando autorización del SRI...');
                        for (let i = 0; i < 5; i++) {
                            await new Promise(r => setTimeout(r, 1000));
                            try {
                                const statusCheck = await billingService.getOrderInvoiceStatus(createdOrder.id);
                                const sd = statusCheck?.data || statusCheck;
                                const key = sd?.access_key || sd?.sri_access_key;
                                if (key) {
                                    receiptData.sri_access_key = key;
                                    receiptData.sri_document_number = sd.document_number || sd.sri_document_number || receiptData.sri_document_number;
                                    console.log(`Clave de acceso obtenida en intento ${i+1}:`, key.slice(0,10) + '...');
                                    break;
                                }
                            } catch (_) {}
                        }
                    }
                } catch (billingErr) {
                    console.warn('Nota sobre facturación SRI:', billingErr.response?.data || billingErr);
                }
            } else if (documentType === 'nota_entrega') {
                console.log('Orden procesada como Nota de Entrega local (Sin emisión SRI).');
            }

            // 4. ENVIAR A IMPRIMIR
            setPrintStep('Enviando a impresora...');
            try {
                const printResult = await printerService.printReceipt(receiptData);
                console.log('Ticket enviado a impresión:', printResult);
                setPrintStep('');

                alert(
                    `¡Orden enviada a preparación!\n\n` +
                    (createdOrder.order_sequence ? `Turno / Orden: #${createdOrder.order_sequence}\n` : '') +
                    `Estado: En Preparación (Pago Pendiente)\n` +
                    `Ref: ${createdOrder.order_number || createdOrder.id}\n` +
                    (receiptData.sri_access_key ? `Clave de Acceso: ${receiptData.sri_access_key}\n` : '') +
                    `Ticket: ${printResult.job_number}\n\n` +
                    `El cobro se realizará desde el módulo de Órdenes al entregar el pedido.`
                );
            } catch (printError) {
                console.error('Error al imprimir:', printError);
                setPrintStep('');
                const backendMsg = printError.response?.data?.error || printError.response?.data?.message || printError.message || 'Error desconocido';
                alert(
                    `Orden creada pero ocurrió un detalle al imprimir:\n\n` +
                    `Orden: ${createdOrder.order_number || createdOrder.id}\n` +
                    `Detalle: ${backendMsg}\n\n` +
                    `Verifica que la impresora esté conectada o que la app RawBT esté abierta.`
                );
            }

            // 4. LIMPIAR EL CARRITO Y ESTADOS DE PAGO
            setCart([]);

            setAppliedDiscount(null);
            setDiscountCode('');
            setIsEmployeeDiscount(false);
            setOrderDiscountPercentage(0);
            setSelectedTable('');
            setSelectedCustomer(null);
            setCustomerSearch('');
            setCashGiven(null); // Resetear calculadora
            setInputCash('');
            setCurrency('USD'); // Resetear moneda a USD
            setDocumentType('factura'); // Resetear tipo de documento
            
            // LIMPIEZA ADICIONAL PARA EVITAR ARRASTRE DE DATOS
            setPaymentMethod('efectivo');
            setTransferReference('');
            setSplit1Method('efectivo');
            setSplit1Currency('USD');
            setSplit1Amount('');
            setSplit2Method('transferencia');
            setSplit2Currency('COP');
            setSplit2Amount('');
            setShowReviewModal(false);

        } catch (err) {
            console.error('Error al procesar la orden:', err);
            const errorMsg = err.response?.data
                ? JSON.stringify(err.response.data)
                : 'Error al procesar la orden';
            alert(`Error: ${errorMsg}`);
        } finally {
            setProcessingOrder(false);
        }
    };

    // FUNCIÓN PARA ABRIR CAJA MANUALMENTE
    const handleOpenCashDrawer = async () => {
        try {
            await printerService.openCashDrawer();
            alert('Caja abierta');
        } catch (error) {
            alert('Error al abrir caja. Verifica que el agente esté ejecutándose.');
        }
    };

    const openOrderConfirmationModal = () => {
        if (cart.length === 0) {
            alert("El carrito está vacío.");
            return;
        }
        setShowReviewModal(true);
    };

    // =====================================
    // 10. COMPONENTES DE RENDERIZADO
    // =====================================

    const renderReviewDetails = () => (
        <div style={{ padding: screenWidth <= 1366 ? '0.5rem' : '0 1rem' }}>
            {/* Sección de configuración de orden */}
            <div style={{
                backgroundColor: 'var(--sidebar-bg)',
                borderRadius: '8px',
                padding: screenWidth <= 1366 ? '0.75rem' : '1rem',
                marginBottom: '1rem'
            }}>
                {/* Mesa/Tipo de Orden */}
                <div>
                    <label style={{
                        display: 'block',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.875rem',
                        fontWeight: '600',
                        color: '#4a3f36',
                        marginBottom: '0.5rem'
                    }}>
                        Mesa / Tipo de Orden
                    </label>
                    <select
                        style={{
                            width: '100%',
                            padding: screenWidth <= 1366 ? '0.5rem' : '0.75rem',
                            border: '2px solid #d6ccbc',
                            borderRadius: '8px',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            color: 'var(--primary-color)',
                            backgroundColor: '#ffffff',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        value={selectedTable}
                        onChange={(e) => setSelectedTable(e.target.value)}
                    >
                        <option value="">Seleccionar mesa...</option>
                        <option value="takeout">Para Llevar (Takeout)</option>
                        {tables.map(table => (
                            <option
                                key={table.id}
                                value={table.number}
                                disabled={table.status !== 'available'}
                            >
                                Mesa {table.number} {table.status !== 'available' ? '(Ocupada)' : ''}
                            </option>
                        ))}
                    </select>
                </div>

            </div>

            <div style={{
                maxHeight: screenWidth <= 1366 ? '40vh' : '30vh',
                overflowY: 'auto',
                marginBottom: '1rem',
                fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem'
            }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: 'var(--sidebar-bg)' }}>
                        <tr>
                            <th style={{
                                textAlign: 'left',
                                padding: screenWidth <= 1366 ? '0.25rem' : '0.5rem',
                                fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                color: '#6b5e51'
                            }}>PRODUCTO</th>
                            <th style={{
                                width: '15%',
                                textAlign: 'right',
                                padding: screenWidth <= 1366 ? '0.25rem' : '0.5rem',
                                fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                color: '#6b5e51'
                            }}>CANT.</th>
                            <th style={{
                                width: '25%',
                                textAlign: 'right',
                                padding: screenWidth <= 1366 ? '0.25rem' : '0.5rem',
                                fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                color: '#6b5e51'
                            }}>TOTAL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cart.map((item, index) => (
                            <React.Fragment key={index}>
                                <tr>
                                    <td style={{
                                        padding: screenWidth <= 1366 ? '0.25rem 0' : '0.5rem 0',
                                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem'
                                    }}>
                                        <div>
                                            {item.name}
                                            {item.note && (
                                                <div style={{
                                                    fontSize: screenWidth <= 1366 ? '0.75rem' : '0.8rem',
                                                    color: '#8a7d70',
                                                    fontStyle: 'italic',
                                                    marginTop: '2px'
                                                }}>
                                                    ({item.note})
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={{
                                        textAlign: 'right',
                                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem'
                                    }}>{item.quantity}</td>
                                    <td style={{
                                        textAlign: 'right',
                                        fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9rem',
                                        fontWeight: '600'
                                    }}>
                                        {item.discount_percentage > 0 && (
                                            <div style={{ textDecoration: 'line-through', color: '#a89c8c', fontSize: '0.75rem', fontWeight: 'normal' }}>
                                                {formatCurrency(item.price * item.quantity)}
                                            </div>
                                        )}
                                        <div style={{ color: item.discount_percentage > 0 ? '#5f8a5e' : 'inherit' }}>
                                            {formatCurrency(item.price * item.quantity * (1 - (parseFloat(item.discount_percentage) || 0) / 100))}
                                        </div>
                                    </td>
                                </tr>
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* INFORMATIVO ESTADO DE ORDEN */}
            <div style={{
                marginTop: '1rem',
                padding: '1rem',
                backgroundColor: '#fcf7ea',
                borderRadius: '10px',
                border: '1px solid #f7ecd3',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
            }}>
                <div style={{
                    width: '42px',
                    height: '42px',
                    borderRadius: '50%',
                    backgroundColor: '#f7ecd3',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.4rem',
                    flexShrink: 0
                }}>
                    <i className="bi bi-fire"></i>
                </div>
                <div>
                    <div style={{ fontWeight: '700', color: '#6f4d17', fontSize: '0.95rem' }}>
                        Paso 1: Enviar a Preparación
                    </div>
                    <div style={{ fontSize: '0.825rem', color: '#8f6520', marginTop: '2px', lineHeight: '1.3' }}>
                        La orden pasará directamente a <strong>En Preparación</strong>. Cliente, descuentos y cobro se registran en <strong>Órdenes</strong>.
                    </div>
                </div>
            </div>

            {/* RESUMEN DE TOTALES */}
            <div style={{ borderTop: '2px solid #e6dfd3', paddingTop: '1rem', marginTop: '1rem' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem',
                    color: '#8a7d70'
                }}>
                    <span>Subtotal</span>
                    <span>{formatCurrency(calculateSubtotal)}</span>
                </div>

                {appliedDiscount && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem',
                        color: '#b04a3d',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem'
                    }}>
                        <span>Descuento</span>
                        <span>- {formatCurrency(calculateDiscountAmount)}</span>
                    </div>
                )}

                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    fontSize: screenWidth <= 1366 ? '1.25rem' : '1.5rem',
                    fontWeight: 'bold',
                    borderTop: '1px solid #ccc',
                    paddingTop: '0.75rem'
                }}>
                    <span>Total de la Orden</span>
                    <div style={{ textAlign: 'right' }}>
                        <span style={{ color: 'var(--primary-color)' }}>{formatCurrency(calculateTotal)}</span>
                        {exchangeRate && (
                            <div style={{ fontSize: '0.8rem', color: '#8a7d70', fontWeight: '500' }}>
                                Ref: COP {(calculateTotal * exchangeRate).toLocaleString('es-CO')}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // Modal para agregar nota
    const renderNoteModal = () => (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1001,
            padding: screenWidth <= 1366 ? '0.5rem' : '1rem'
        }}>
            <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                width: '100%',
                maxWidth: screenWidth <= 1366 ? '95%' : '500px',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                padding: screenWidth <= 1366 ? '1rem' : '1.5rem'
            }}>
                <div style={{
                    marginBottom: '1rem',
                    borderBottom: '2px solid #e6dfd3',
                    paddingBottom: '0.75rem'
                }}>
                    <h3 style={{
                        fontSize: screenWidth <= 1366 ? '1.125rem' : '1.25rem',
                        fontWeight: '700',
                        color: '#1f1f1f',
                        margin: 0
                    }}>
                        Agregar Nota Especial
                    </h3>
                    <p style={{
                        fontSize: screenWidth <= 1366 ? '0.75rem' : '0.875rem',
                        color: '#8a7d70',
                        marginTop: '0.25rem'
                    }}>
                        Escribe las especificaciones del producto (aparecerá entre paréntesis en el ticket)
                    </p>
                </div>

                <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Ej: Sin cebolla, extra queso, bien cocido, etc."
                    style={{
                        width: '100%',
                        height: '120px',
                        padding: '0.75rem',
                        border: '2px solid #d6ccbc',
                        borderRadius: '8px',
                        fontSize: screenWidth <= 1366 ? '0.875rem' : '1rem',
                        fontFamily: 'inherit',
                        resize: 'none',
                        boxSizing: 'border-box',
                        marginBottom: '1.5rem'
                    }}
                    maxLength={100}
                />

                <div style={{
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                }}>
                    <button
                        style={{
                            padding: screenWidth <= 1366 ? '0.5rem 1rem' : '0.75rem 1.5rem',
                            backgroundColor: '#ffffff',
                            border: '2px solid #d6ccbc',
                            borderRadius: '8px',
                            color: '#4a3f36',
                            fontWeight: '600',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        onClick={cancelNote}
                    >
                        Cancelar
                    </button>
                    <button
                        style={{
                            padding: screenWidth <= 1366 ? '0.5rem 1rem' : '0.75rem 1.5rem',
                            backgroundColor: 'var(--primary-color)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#ffffff',
                            fontWeight: '600',
                            fontSize: screenWidth <= 1366 ? '0.875rem' : '0.9375rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            minHeight: TOUCH_MIN_SIZE
                        }}
                        onClick={saveNote}
                    >
                        Guardar Nota
                    </button>
                </div>
            </div>
        </div>
    );

    // ====== OVERLAY DE CARGA ANIMADO (Muestra el paso actual durante la venta) ======
    const renderPrintOverlay = () => {
        if (!printStep) return null;
        return (
            <div style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.65)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999
            }}>
                <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '16px',
                    padding: '2.5rem 2rem',
                    textAlign: 'center',
                    maxWidth: '340px',
                    width: '90%',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.4)'
                }}>
                    {/* Spinner */}
                    <div style={{
                        width: '56px', height: '56px', margin: '0 auto 1.25rem',
                        border: '5px solid #e6dfd3',
                        borderTop: '5px solid var(--primary-color, #6b4a33)',
                        borderRadius: '50%',
                        animation: 'spin 0.9s linear infinite'
                    }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <p style={{ fontSize: '1rem', fontWeight: '600', color: '#1f1f1f', margin: 0 }}>
                        {printStep}
                    </p>
                    <p style={{ fontSize: '0.8rem', color: '#8a7d70', marginTop: '0.5rem' }}>
                        Por favor espera, no cierres la pantalla...
                    </p>
                </div>
            </div>
        );
    };

    // Renderizar vista con botones abajo (para pantallas <= 1366px - menos de 16 pulgadas)
    const renderCompactView = () => (
        <div style={{
            height: '100dvh', // Usar dvh para móviles/tablets
            maxHeight: '-webkit-fill-available', // Fallback iOS
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--sidebar-bg)',
            overflow: 'hidden',
            position: 'fixed', // Fijar viewport
            top: 0,
            left: 0,
            right: 0,
            bottom: 0
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#ffffff',
                borderBottom: '2px solid #e6dfd3',
                padding: '0.75rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                flexShrink: 0,
                zIndex: 10
            }}>
                <h1 style={{
                    fontSize: screenWidth <= 768 ? '1.25rem' : '1.5rem',
                    fontWeight: '700',
                    color: '#1f1f1f',
                    margin: 0,
                    textAlign: 'center'
                }}>
                    Punto de Venta
                </h1>
            </div>

            {/* Contenido principal - Alterna entre productos y orden */}
            {!showOrderDetails ? (
                // Vista de productos
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0, // Clave para que el scroll interno funcione en flex
                    overflow: 'hidden'
                }}>
                    {/* Filtros */}
                    <div style={{
                        padding: '0.75rem',
                        borderBottom: '1px solid #e6dfd3',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0
                    }}>
                        <div style={{
                            display: 'flex',
                            overflowX: 'auto',
                            gap: '0.5rem',
                            paddingBottom: '0.25rem'
                        }}>
                            <button
                                style={{
                                    padding: '0.5rem 1rem',
                                    borderRadius: '6px',
                                    border: selectedCategory === 'all' ? 'none' : '2px solid #d6ccbc',
                                    backgroundColor: selectedCategory === 'all' ? 'var(--primary-color)' : '#ffffff',
                                    color: selectedCategory === 'all' ? '#ffffff' : '#4a3f36',
                                    fontWeight: '600',
                                    fontSize: '0.875rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setSelectedCategory('all')}
                            >
                                Todos
                            </button>
                            {promotions.length > 0 && (
                                <button
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === 'promotions' ? 'none' : '2px solid #d4a94a',
                                        backgroundColor: selectedCategory === 'promotions' ? '#c9973a' : '#fcf7ea',
                                        color: selectedCategory === 'promotions' ? '#ffffff' : '#8f6520',
                                        fontWeight: '700',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory('promotions')}
                                >
                                    Promociones
                                </button>
                            )}
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === cat.id ? 'none' : '2px solid #d6ccbc',
                                        backgroundColor: selectedCategory === cat.id ? 'var(--primary-color)' : '#ffffff',
                                        color: selectedCategory === cat.id ? '#ffffff' : '#4a3f36',
                                        fontWeight: '600',
                                        fontSize: '0.875rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name.length > 12 ? cat.name.substring(0, 10) + '...' : cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Grid Productos */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        backgroundColor: 'var(--sidebar-bg)'
                    }}>
                        {renderPromoSubcategories()}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: screenWidth <= 768 ?
                                'repeat(auto-fill, minmax(140px, 1fr))' :
                                'repeat(auto-fill, minmax(160px, 1fr))',
                            gap: '0.75rem'
                        }}>
                            {(selectedCategory === 'promotions' ? filteredPromotions : filteredProducts).map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: '1px solid #e6dfd3',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onClick={() => selectedCategory === 'promotions' ? handleAddPromo(item) : handleProductClick(item)}
                                    onMouseEnter={screenWidth > 768 ? (e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                        e.currentTarget.style.borderColor = selectedCategory === 'promotions' ? '#c9973a' : 'var(--primary-color)';
                                    } : undefined}
                                    onMouseLeave={screenWidth > 768 ? (e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        e.currentTarget.style.borderColor = '#e6dfd3';
                                    } : undefined}
                                >
                                    <div style={{
                                        height: screenWidth <= 768 ? '80px' : '100px',
                                        backgroundColor: 'var(--sidebar-bg)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.5rem'
                                    }}>
                                        {item.image ? (
                                            <img
                                                src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_FAST_FOOD_SERVICE}${item.image}`}
                                                alt={item.name}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <span style={{
                                                color: '#a89c8c',
                                                fontSize: '0.75rem',
                                                textAlign: 'center'
                                            }}>
                                                Sin imagen
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ padding: '0.5rem' }}>
                                        <h3 style={{
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                            fontWeight: '600',
                                            color: selectedCategory === 'promotions' ? '#8f6520' : 'var(--primary-color)',
                                            marginBottom: '0.25rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {item.name}
                                        </h3>
                                        {item.description && (
                                            <p style={{
                                                fontSize: '0.75rem',
                                                color: '#8a7d70',
                                                marginBottom: '0.25rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {item.description}
                                            </p>
                                        )}
                                        <p style={{
                                            fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                            fontWeight: '700',
                                            color: selectedCategory === 'promotions' ? '#8f6520' : 'var(--primary-color)',
                                            margin: 0
                                        }}>
                                            ${item.price}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ) : (
                // Vista de orden
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>
                    {/* Header de orden */}
                    <div style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        borderBottom: '1px solid #e6dfd3',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <h3 style={{
                            fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                            fontWeight: '700',
                            color: '#1f1f1f',
                            margin: 0
                        }}>
                            Orden Actual ({cart.length})
                        </h3>
                        <button
                            style={{
                                padding: '0.5rem 1rem',
                                backgroundColor: 'var(--primary-color)',
                                border: 'none',
                                borderRadius: '6px',
                                color: '#ffffff',
                                fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                minHeight: TOUCH_MIN_SIZE
                            }}
                            onClick={() => setShowOrderDetails(false)}
                        >
                            ← Productos
                        </button>
                    </div>

                    {/* Contenido del carrito */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '0.75rem',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {cart.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#a89c8c',
                                fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem'
                            }}>
                                <p style={{ margin: 0 }}>No hay productos en el carrito</p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem',
                                flex: 1
                            }}>
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e6dfd3',
                                            borderRadius: '8px',
                                            padding: '0.75rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.5rem'
                                        }}
                                    >
                                        {/* Información del producto */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.25rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <p style={{
                                                    fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                    color: '#8a7d70',
                                                    margin: 0
                                                }}>
                                                    {item.discount_percentage > 0 ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', marginRight: '0.25rem', color: '#a89c8c' }}>
                                                                {formatCurrency(item.price)}
                                                            </span>
                                                            <span style={{ color: '#5f8a5e', fontWeight: 'bold' }}>
                                                                {formatCurrency(item.price * (1 - (parseFloat(item.discount_percentage) || 0) / 100))} c/u
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>{formatCurrency(item.price)} c/u</>
                                                    )}
                                                </p>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '2px solid #e6dfd3',
                                                    borderRadius: '6px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <button
                                                        style={{
                                                            width: screenWidth <= 768 ? '36px' : '40px',
                                                            height: screenWidth <= 768 ? '36px' : '40px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#8a7d70',
                                                            fontSize: screenWidth <= 768 ? '1rem' : '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{
                                                        width: screenWidth <= 768 ? '30px' : '34px',
                                                        textAlign: 'center',
                                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                                        fontWeight: '600',
                                                        color: 'var(--primary-color)'
                                                    }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        style={{
                                                            width: screenWidth <= 768 ? '36px' : '40px',
                                                            height: screenWidth <= 768 ? '36px' : '40px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#8a7d70',
                                                            fontSize: screenWidth <= 768 ? '1rem' : '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    style={{
                                                        width: screenWidth <= 768 ? '36px' : '40px',
                                                        height: screenWidth <= 768 ? '36px' : '40px',
                                                        backgroundColor: '#f7e5e0',
                                                        border: '2px solid #f0cfc5',
                                                        borderRadius: '6px',
                                                        color: '#b04a3d',
                                                        fontSize: screenWidth <= 768 ? '1rem' : '1.125rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={() => removeFromCart(item)}
                                                    title="Eliminar producto"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>

                                        {/* Nota y descuento del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '0.5rem',
                                            borderTop: '1px dashed #e6dfd3'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                {item.note ? (
                                                    <div style={{
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        color: '#8a7d70',
                                                        fontStyle: 'italic',
                                                        backgroundColor: 'var(--sidebar-bg)',
                                                        padding: '0.25rem 0.5rem',
                                                        borderRadius: '4px',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        <strong>Nota:</strong> {item.note}
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        color: '#a89c8c',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Sin notas (mt: {item.max_toppings})
                                                    </span>
                                                )}
                                            </div>

                                            {/* Variables seleccionadas */}
                                            {item.selectedVariables && item.selectedVariables.length > 0 && (
                                                <div style={{ padding: '0.25rem 0', width: '100%' }}>
                                                    {item.selectedVariables.map((v, idx) => (
                                                        <div key={idx} style={{
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            color: '#6b5e51',
                                                            backgroundColor: '#f4efe6',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            display: 'inline-block',
                                                            marginRight: '4px',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <strong>{v.variable}:</strong> {v.option}
                                                            {v.extra_price > 0 && ` (+$${v.extra_price.toFixed(2)})`}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flexWrap: 'wrap' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #d6ccbc',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <span style={{ padding: '0.25rem', backgroundColor: '#f4efe6', color: '#8a7d70', fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem' }}>%</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.discount_percentage || ''}
                                                        onChange={(e) => updateItemDiscount(item, e.target.value)}
                                                        placeholder="0"
                                                        style={{
                                                            width: '32px',
                                                            border: 'none',
                                                            padding: '0.25rem',
                                                            textAlign: 'center',
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>

                                                <button
                                                    style={{
                                                        padding: '0.25rem 0.5rem',
                                                        backgroundColor: item.note ? '#f7ecd3' : 'var(--sidebar-bg)',
                                                        border: `1px solid ${item.note ? '#d4a94a' : '#d6ccbc'}`,
                                                        borderRadius: '4px',
                                                        color: item.note ? '#6f4d17' : '#4a3f36',
                                                        fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        marginLeft: '0.25rem',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: TOUCH_MIN_SIZE
                                                    }}
                                                    onClick={() => handleAddNote(item)}
                                                    title={item.note ? "Editar nota" : "Agregar nota"}
                                                >
                                                    {item.note ? 'Editar' : 'Nota'}
                                                </button>
                                                
                                                    <button
                                                        onClick={() => {
                                                            setActiveToppingsItemIndex(index);
                                                            setShowToppingsModal(true);
                                                        }}
                                                        style={{
                                                            padding: '0.25rem 0.5rem',
                                                            backgroundColor: item.selectedToppings?.length > 0 ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                            border: `1px solid ${item.selectedToppings?.length > 0 ? 'var(--primary-color)' : '#d6ccbc'}`,
                                                            borderRadius: '4px',
                                                            color: item.selectedToppings?.length > 0 ? '#fff' : '#4a3f36',
                                                            fontSize: screenWidth <= 768 ? '0.75rem' : '0.8125rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            marginLeft: '0.25rem',
                                                            whiteSpace: 'nowrap',
                                                            minHeight: TOUCH_MIN_SIZE
                                                        }}
                                                        title="Agregar toppings"
                                                    >
                                                        Toppings {item.selectedToppings?.length > 0 ? `(${item.selectedToppings.length}/${item.max_toppings})` : ''}
                                                    </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Totales y Botones */}
                        {cart.length > 0 && (
                            <div style={{
                                marginTop: 'auto',
                                paddingTop: '1rem',
                                borderTop: '2px solid #e6dfd3'
                            }}>
                                {/* Totales */}
                                <div style={{
                                    paddingBottom: '1rem',
                                    marginBottom: '0.75rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                        color: '#8a7d70'
                                    }}>
                                        <span>Subtotal</span>
                                        <span style={{ fontWeight: '600' }}>{currency === 'COP' ? `COP ${(calculateSubtotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateSubtotal)}</span>
                                    </div>

                                    {appliedDiscount && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '0.5rem',
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                            color: '#b04a3d',
                                            fontWeight: '600'
                                        }}>
                                            <span>Descuento</span>
                                            <span>- {formatCurrency(calculateDiscountAmount)}</span>
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        paddingTop: '0.75rem',
                                        borderTop: '2px solid #e6dfd3',
                                        fontSize: screenWidth <= 768 ? '1.25rem' : '1.5rem',
                                        fontWeight: '700',
                                        color: '#1f1f1f'
                                    }}>
                                        <span>Total</span>
                                        <span style={{ color: 'var(--primary-color)' }}>{currency === 'COP' ? `COP ${(calculateTotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateTotal)}</span>
                                    </div>
                                </div>

                                {/* Botones principales */}
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <button
                                        style={{
                                            flex: 1,
                                            padding: '0.75rem',
                                            backgroundColor: '#c9973a',
                                            border: 'none',
                                            borderRadius: '8px',
                                            color: '#ffffff',
                                            fontSize: screenWidth <= 768 ? '0.875rem' : '0.95rem',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            minHeight: TOUCH_MIN_SIZE,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '4px'
                                        }}
                                        onClick={handleHoldCurrentOrder}
                                        title="Poner orden actual en espera"
                                    >
                                        En Espera
                                    </button>

                                    <button
                                        style={{
                                            flex: 2,
                                            padding: '0.75rem',
                                            backgroundColor: processingOrder ? '#d6ccbc' : 'var(--primary-color)',
                                            border: 'none',
                                            borderRadius: '8px',
                                            color: '#ffffff',
                                            fontSize: screenWidth <= 768 ? '0.95rem' : '1.05rem',
                                            fontWeight: '700',
                                            cursor: processingOrder ? 'not-allowed' : 'pointer',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                        onClick={openOrderConfirmationModal}
                                        disabled={processingOrder}
                                    >
                                        {processingOrder ? 'Procesando...' : editOrder ? `Guardar cambios${editOrder.order_sequence ? ` (#${editOrder.order_sequence})` : ''}` : 'Confirmar Pedido'}
                                    </button>
                                </div>

                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={handleOpenCashDrawer}
                                >
                                    Abrir Caja
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Barra inferior con botones */}
            <div style={{
                backgroundColor: '#ffffff',
                borderTop: '2px solid #e6dfd3',
                padding: '0.5rem',
                display: 'flex',
                gap: '0.5rem',
                flexShrink: 0
            }}>
                <button
                    style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: showOrderDetails ? '#e6dfd3' : 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '8px',
                        color: showOrderDetails ? '#4a3f36' : '#ffffff',
                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minHeight: TOUCH_MIN_SIZE
                    }}
                    onClick={() => setShowOrderDetails(false)}
                >
                    Productos
                </button>
                <button
                    style={{
                        flex: 1,
                        padding: '0.75rem',
                        backgroundColor: !showOrderDetails ? '#e6dfd3' : 'var(--primary-color)',
                        border: 'none',
                        borderRadius: '8px',
                        color: !showOrderDetails ? '#4a3f36' : '#ffffff',
                        fontSize: screenWidth <= 768 ? '0.875rem' : '0.9375rem',
                        fontWeight: '600',
                        cursor: 'pointer',
                        minHeight: TOUCH_MIN_SIZE,
                        position: 'relative'
                    }}
                    onClick={() => setShowOrderDetails(true)}
                >
                    Orden {cart.length > 0 && (
                        <span style={{
                            position: 'absolute',
                            top: '-5px',
                            right: '-5px',
                            backgroundColor: '#c0574a',
                            color: '#ffffff',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {cart.length}
                        </span>
                    )}
                </button>
            </div>
        </div>
    );

    // Renderizar vista de escritorio dividida (para pantallas > 1366px - más de 16 pulgadas)
    const renderDesktopView = () => (
        <div style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'var(--sidebar-bg)',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                backgroundColor: '#ffffff',
                borderBottom: '2px solid #e6dfd3',
                padding: '1rem 1.5rem',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div>
                    <h1 style={{
                        fontSize: '1.75rem',
                        fontWeight: '700',
                        color: '#1f1f1f',
                        margin: 0,
                        letterSpacing: '-0.025em'
                    }}>
                        Punto de Venta
                    </h1>
                </div>

                {/* Pestaña / Botón de Órdenes en Espera y Configuración de Secuencia */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={() => setShowSequenceModal(true)}
                            style={{
                                padding: '0.6rem 1rem',
                                backgroundColor: '#f2f7ee',
                                color: '#3d5c37',
                                border: '2px solid #a9c9a0',
                                borderRadius: '8px',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                transition: 'all 0.2s',
                                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                            }}
                            title="Configurar número correlativo de comanda y reinicio diario"
                        >
                            <span><i className="bi bi-123"></i></span>
                            <span>Turno / N° Orden</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => setShowHeldOrdersModal(true)}
                        style={{
                            padding: '0.6rem 1.2rem',
                            backgroundColor: heldOrders.length > 0 ? '#f7ecd3' : '#f4efe6',
                            color: heldOrders.length > 0 ? '#8f6520' : '#8a7d70',
                            border: `2px solid ${heldOrders.length > 0 ? '#c9973a' : '#d6ccbc'}`,
                            borderRadius: '8px',
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.6rem',
                            transition: 'all 0.2s',
                            boxShadow: heldOrders.length > 0 ? '0 2px 8px rgba(201, 151, 58, 0.2)' : 'none'
                        }}
                    >
                        <i className="bi bi-pause-circle-fill" style={{ fontSize: '1.1rem', color: heldOrders.length > 0 ? '#c9973a' : '#a89c8c' }}></i>
                        <span>Órdenes en Espera</span>
                        <span style={{
                            backgroundColor: heldOrders.length > 0 ? '#b07f2b' : '#a89c8c',
                            color: 'white',
                            borderRadius: '12px',
                            padding: '2px 8px',
                            fontSize: '0.85rem',
                            fontWeight: 800
                        }}>
                            {heldOrders.length}
                        </span>
                    </button>
                </div>
            </div>

            <div style={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
                flexDirection: 'row'
            }}>
                {/* Panel Izquierdo: Catálogo */}
                <div style={{
                    flex: '1 1 60%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: '#ffffff',
                    borderRight: '2px solid #e6dfd3',
                    minHeight: 'auto',
                    minWidth: 0,
                    maxWidth: '100%'
                }}>
                    {/* Filtros */}
                    <div style={{
                        padding: '1rem',
                        borderBottom: '1px solid #e6dfd3',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        maxWidth: '100%',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            display: 'flex',
                            gap: '0.75rem',
                            overflowX: 'auto',
                            paddingBottom: '0.25rem',
                            width: '100%'
                        }}>
                            <button
                                style={{
                                    padding: '0.625rem 1.25rem',
                                    borderRadius: '6px',
                                    border: selectedCategory === 'all' ? 'none' : '2px solid #d6ccbc',
                                    backgroundColor: selectedCategory === 'all' ? 'var(--primary-color)' : '#ffffff',
                                    color: selectedCategory === 'all' ? '#ffffff' : '#4a3f36',
                                    fontWeight: '600',
                                    fontSize: '0.9375rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    whiteSpace: 'nowrap',
                                    boxShadow: selectedCategory === 'all' ? '0 2px 4px rgba(74, 50, 34, 0.3)' : 'none',
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setSelectedCategory('all')}
                            >
                                Todos los productos
                            </button>
                            {promotions.length > 0 && (
                                <button
                                    style={{
                                        padding: '0.625rem 1.25rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === 'promotions' ? 'none' : '2px solid #d4a94a',
                                        backgroundColor: selectedCategory === 'promotions' ? '#c9973a' : '#fcf7ea',
                                        color: selectedCategory === 'promotions' ? '#ffffff' : '#8f6520',
                                        fontWeight: '700',
                                        fontSize: '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        boxShadow: selectedCategory === 'promotions' ? '0 2px 4px rgba(201, 151, 58, 0.3)' : 'none',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory('promotions')}
                                >
                                    Promociones
                                </button>
                            )}
                            {categories.map(cat => (
                                <button
                                    key={cat.id}
                                    style={{
                                        padding: '0.625rem 1.25rem',
                                        borderRadius: '6px',
                                        border: selectedCategory === cat.id ? 'none' : '2px solid #d6ccbc',
                                        backgroundColor: selectedCategory === cat.id ? 'var(--primary-color)' : '#ffffff',
                                        color: selectedCategory === cat.id ? '#ffffff' : '#4a3f36',
                                        fontWeight: '600',
                                        fontSize: '0.9375rem',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        whiteSpace: 'nowrap',
                                        boxShadow: selectedCategory === cat.id ? '0 2px 4px rgba(74, 50, 34, 0.3)' : 'none',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={() => setSelectedCategory(cat.id)}
                                >
                                    {cat.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Grid Productos */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '1.5rem',
                        backgroundColor: 'var(--sidebar-bg)'
                    }}>
                        {renderPromoSubcategories()}
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                            gap: '1rem'
                        }}>
                            {(selectedCategory === 'promotions' ? filteredPromotions : filteredProducts).map(item => (
                                <div
                                    key={item.id}
                                    style={{
                                        backgroundColor: '#ffffff',
                                        borderRadius: '10px',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        border: '1px solid #e6dfd3',
                                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                                    }}
                                    onClick={() => selectedCategory === 'promotions' ? handleAddPromo(item) : handleProductClick(item)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
                                        e.currentTarget.style.borderColor = selectedCategory === 'promotions' ? '#c9973a' : 'var(--primary-color)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.1)';
                                        e.currentTarget.style.borderColor = '#e6dfd3';
                                    }}
                                >
                                    <div style={{
                                        height: '140px',
                                        backgroundColor: 'var(--sidebar-bg)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.75rem'
                                    }}>
                                        {item.image ? (
                                            <img
                                                src={item.image.startsWith('http') ? item.image : `${process.env.REACT_APP_FAST_FOOD_SERVICE}${item.image}`}
                                                alt={item.name}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <span style={{
                                                color: '#a89c8c',
                                                fontSize: '0.75rem',
                                                textAlign: 'center'
                                            }}>
                                                Sin imagen
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ padding: '0.875rem' }}>
                                        <h3 style={{
                                            fontSize: '0.9375rem',
                                            fontWeight: '600',
                                            color: selectedCategory === 'promotions' ? '#8f6520' : 'var(--primary-color)',
                                            marginBottom: '0.375rem',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {item.name}
                                        </h3>
                                        {item.description && (
                                            <p style={{
                                                fontSize: '0.75rem',
                                                color: '#8a7d70',
                                                marginBottom: '0.375rem',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical'
                                            }}>
                                                {item.description}
                                            </p>
                                        )}
                                        <p style={{
                                            fontSize: '1.125rem',
                                            fontWeight: '700',
                                            color: selectedCategory === 'promotions' ? '#8f6520' : 'var(--primary-color)',
                                            margin: 0
                                        }}>
                                            ${item.price}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Panel Derecho: Orden Actual */}
                <div style={{
                    flex: screenWidth <= 1024 ? '0 0 320px' : '0 0 400px',
                    backgroundColor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: '-2px 0 8px rgba(0, 0, 0, 0.05)',
                    flexShrink: 0
                }}>
                    {/* Header de Orden Actual */}
                    <div style={{
                        padding: '1rem 1.5rem',
                        backgroundColor: 'var(--sidebar-bg)',
                        flexShrink: 0,
                        borderBottom: '1px solid #e6dfd3'
                    }}>
                        <h3 style={{
                            fontSize: '1.125rem',
                            fontWeight: '700',
                            color: '#1f1f1f',
                            margin: 0
                        }}>
                            Orden Actual
                        </h3>
                    </div>

                    {/* Contenido del Carrito */}
                    <div style={{
                        flex: 1,
                        overflowY: 'auto',
                        padding: '1.5rem',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        {cart.length === 0 ? (
                            <div style={{
                                textAlign: 'center',
                                padding: '3rem 1rem',
                                color: '#a89c8c',
                                fontSize: '0.875rem'
                            }}>
                                <p style={{ margin: 0 }}>No hay productos en el carrito</p>
                                <p style={{
                                    margin: '0.5rem 0 0 0',
                                    fontSize: '0.8125rem'
                                }}>
                                    Selecciona productos para comenzar
                                </p>
                            </div>
                        ) : (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.75rem',
                                flex: 1
                            }}>
                                {cart.map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            backgroundColor: '#ffffff',
                                            border: '1px solid #e6dfd3',
                                            borderRadius: '10px',
                                            padding: '1rem',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '0.75rem'
                                        }}
                                    >
                                        {/* Información del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: '0.75rem'
                                        }}>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <h4 style={{
                                                    fontSize: '0.9375rem',
                                                    fontWeight: '600',
                                                    color: 'var(--primary-color)',
                                                    marginBottom: '0.375rem',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {item.name}
                                                </h4>
                                                <p style={{
                                                    fontSize: '0.8125rem',
                                                    color: '#8a7d70',
                                                    margin: 0
                                                }}>
                                                    {item.discount_percentage > 0 ? (
                                                        <>
                                                            <span style={{ textDecoration: 'line-through', marginRight: '0.25rem', color: '#a89c8c' }}>
                                                                {formatCurrency(item.price)}
                                                            </span>
                                                            <span style={{ color: '#5f8a5e', fontWeight: 'bold' }}>
                                                                {formatCurrency(item.price * (1 - (parseFloat(item.discount_percentage) || 0) / 100))} c/u
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <>{formatCurrency(item.price)} c/u</>
                                                    )}
                                                </p>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '0.5rem'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '2px solid #e6dfd3',
                                                    borderRadius: '8px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <button
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#8a7d70',
                                                            fontSize: '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, -1)}
                                                    >
                                                        −
                                                    </button>
                                                    <span style={{
                                                        width: '36px',
                                                        textAlign: 'center',
                                                        fontSize: '0.9375rem',
                                                        fontWeight: '600',
                                                        color: 'var(--primary-color)'
                                                    }}>
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        style={{
                                                            width: '36px',
                                                            height: '36px',
                                                            border: 'none',
                                                            backgroundColor: 'transparent',
                                                            color: '#8a7d70',
                                                            fontSize: '1.25rem',
                                                            fontWeight: '600',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center'
                                                        }}
                                                        onClick={() => updateQuantity(item, 1)}
                                                    >
                                                        +
                                                    </button>
                                                </div>

                                                <button
                                                    style={{
                                                        width: '36px',
                                                        height: '36px',
                                                        backgroundColor: '#f7e5e0',
                                                        border: '2px solid #f0cfc5',
                                                        borderRadius: '8px',
                                                        color: '#b04a3d',
                                                        fontSize: '1.125rem',
                                                        fontWeight: '600',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    onClick={() => removeFromCart(item)}
                                                    title="Eliminar producto"
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        </div>

                                        {/* Nota y descuento del producto */}
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            paddingTop: '0.75rem',
                                            borderTop: '1px dashed #e6dfd3'
                                        }}>
                                            <div style={{ flex: 1 }}>
                                                {item.note ? (
                                                    <div style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#8a7d70',
                                                        fontStyle: 'italic',
                                                        backgroundColor: 'var(--sidebar-bg)',
                                                        padding: '0.375rem 0.75rem',
                                                        borderRadius: '4px',
                                                        wordBreak: 'break-word'
                                                    }}>
                                                        <strong>Nota:</strong> {item.note}
                                                    </div>
                                                ) : (
                                                    <span style={{
                                                        fontSize: '0.8125rem',
                                                        color: '#a89c8c',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        Sin notas especiales
                                                    </span>
                                                )}
                                            </div>

                                            {/* Variables seleccionadas */}
                                            {item.selectedVariables && item.selectedVariables.length > 0 && (
                                                <div style={{ padding: '0.25rem 0', width: '100%' }}>
                                                    {item.selectedVariables.map((v, idx) => (
                                                        <div key={idx} style={{
                                                            fontSize: '0.8125rem',
                                                            color: '#6b5e51',
                                                            backgroundColor: '#f4efe6',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            display: 'inline-block',
                                                            marginRight: '4px',
                                                            marginBottom: '4px'
                                                        }}>
                                                            <strong>{v.variable}:</strong> {v.option}
                                                            {v.extra_price > 0 && ` (+$${v.extra_price.toFixed(2)})`}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    border: '1px solid #d6ccbc',
                                                    borderRadius: '4px',
                                                    overflow: 'hidden',
                                                    backgroundColor: '#ffffff'
                                                }}>
                                                    <span style={{ padding: '0.375rem', backgroundColor: '#f4efe6', color: '#8a7d70', fontSize: '0.8125rem' }}>%</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={item.discount_percentage || ''}
                                                        onChange={(e) => updateItemDiscount(item, e.target.value)}
                                                        placeholder="0"
                                                        style={{
                                                            width: '40px',
                                                            border: 'none',
                                                            padding: '0.375rem',
                                                            textAlign: 'center',
                                                            fontSize: '0.8125rem',
                                                            outline: 'none'
                                                        }}
                                                    />
                                                </div>

                                                <button
                                                    style={{
                                                        padding: '0.375rem 0.75rem',
                                                        backgroundColor: item.note ? '#f7ecd3' : 'var(--sidebar-bg)',
                                                        border: `1px solid ${item.note ? '#d4a94a' : '#d6ccbc'}`,
                                                        borderRadius: '4px',
                                                        color: item.note ? '#6f4d17' : '#4a3f36',
                                                        fontSize: '0.8125rem',
                                                        fontWeight: '500',
                                                        cursor: 'pointer',
                                                        whiteSpace: 'nowrap',
                                                        minHeight: TOUCH_MIN_SIZE,
                                                        minWidth: '60px'
                                                    }}
                                                    onClick={() => handleAddNote(item)}
                                                    title={item.note ? "Editar nota" : "Agregar nota"}
                                                >
                                                    {item.note ? 'Editar' : 'Nota'}
                                                </button>

                                                {item.max_toppings > 0 && (
                                                    <button
                                                        onClick={() => {
                                                            setActiveToppingsItemIndex(index);
                                                            setShowToppingsModal(true);
                                                        }}
                                                        style={{
                                                            padding: '0.375rem 0.75rem',
                                                            backgroundColor: item.selectedToppings?.length > 0 ? 'var(--primary-color)' : 'var(--sidebar-bg)',
                                                            border: `1px solid ${item.selectedToppings?.length > 0 ? 'var(--primary-color)' : '#d6ccbc'}`,
                                                            borderRadius: '4px',
                                                            color: item.selectedToppings?.length > 0 ? '#fff' : '#4a3f36',
                                                            fontSize: '0.8125rem',
                                                            fontWeight: '500',
                                                            cursor: 'pointer',
                                                            whiteSpace: 'nowrap',
                                                            minHeight: TOUCH_MIN_SIZE
                                                        }}
                                                        title="Agregar toppings"
                                                    >
                                                        Toppings {item.selectedToppings?.length > 0 ? `(${item.selectedToppings.length}/${item.max_toppings})` : ''}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Totales y Botones - SOLO se muestra cuando hay productos */}
                        {cart.length > 0 && (
                            <div style={{
                                marginTop: 'auto',
                                paddingTop: '1.5rem',
                                borderTop: '2px solid #e6dfd3'
                            }}>
                                {/* Totales */}
                                <div style={{
                                    paddingBottom: '1rem',
                                    marginBottom: '1rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        fontSize: '0.9375rem',
                                        color: '#8a7d70'
                                    }}>
                                        <span>Subtotal</span>
                                        <span style={{ fontWeight: '600' }}>{currency === 'COP' ? `COP ${(calculateSubtotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateSubtotal)}</span>
                                    </div>

                                    {appliedDiscount && (
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            marginBottom: '0.5rem',
                                            fontSize: '0.9375rem',
                                            color: '#b04a3d',
                                            fontWeight: '600'
                                        }}>
                                            <span>Descuento</span>
                                            <span>- {formatCurrency(calculateDiscountAmount)}</span>
                                        </div>
                                    )}

                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        paddingTop: '1rem',
                                        borderTop: '2px solid #e6dfd3',
                                        fontSize: '1.5rem',
                                        fontWeight: '700',
                                        color: '#1f1f1f'
                                    }}>
                                        <span>Total</span>
                                        <span style={{ color: 'var(--primary-color)' }}>{currency === 'COP' ? `COP ${(calculateTotal * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(calculateTotal)}</span>
                                    </div>
                                </div>

                                {/* Botones Principales */}
                                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                    <button
                                        type="button"
                                        style={{
                                            flex: 1,
                                            padding: '1rem 0.5rem',
                                            backgroundColor: '#c9973a',
                                            border: 'none',
                                            borderRadius: '10px',
                                            color: '#ffffff',
                                            fontSize: '1rem',
                                            fontWeight: '700',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '6px',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                        onClick={handleHoldCurrentOrder}
                                        title="Poner orden actual en espera para atender otro cliente"
                                    >
                                        <i className="bi bi-pause-circle-fill"></i> En Espera
                                    </button>

                                    <button
                                        type="button"
                                        style={{
                                            flex: 2,
                                            padding: '1rem',
                                            backgroundColor: processingOrder ? '#d6ccbc' : 'var(--primary-color)',
                                            border: 'none',
                                            borderRadius: '10px',
                                            color: '#ffffff',
                                            fontSize: '1.125rem',
                                            fontWeight: '700',
                                            cursor: processingOrder ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.2s',
                                            boxShadow: processingOrder ? 'none' : '0 4px 12px rgba(74, 50, 34, 0.3)',
                                            minHeight: TOUCH_MIN_SIZE
                                        }}
                                        onClick={openOrderConfirmationModal}
                                        disabled={processingOrder}
                                    >
                                        {processingOrder ? 'Procesando pedido...' : editOrder ? `Guardar cambios${editOrder.order_sequence ? ` (#${editOrder.order_sequence})` : ''}` : 'Confirmar Pedido'}
                                    </button>
                                </div>

                                {/* Botón Abrir Caja */}
                                <button
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontSize: '0.9375rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={handleOpenCashDrawer}
                                >
                                    Abrir Caja Registradora
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    if (loading || checkingShift) return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            fontSize: '1.125rem',
            color: '#8a7d70'
        }}>
            Cargando sistema de punto de venta...
        </div>
    );

    if (!currentShift || !currentShift.id || currentShift.status !== 'open') {
        return (
            <div style={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--sidebar-bg)',
                padding: '2rem',
                textAlign: 'center'
            }}>
                <i className="bi bi-lock-fill" style={{ fontSize: '5rem', color: '#b04a3d', marginBottom: '1rem' }}></i>
                <h2 style={{ fontSize: '2.5rem', fontWeight: 'bold', color: '#1f1f1f', marginBottom: '1rem' }}>Caja Cerrada</h2>
                <p style={{ fontSize: '1.25rem', color: '#6b5e51', marginBottom: '2.5rem', maxWidth: '600px', lineHeight: '1.5' }}>
                    Debes abrir la caja registradora antes de poder acceder al Punto de Venta y realizar ventas.
                </p>
                <button
                    onClick={() => navigate('/fast-food/shift')}
                    style={{
                        padding: '1rem 2.5rem',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        transition: 'transform 0.2s',
                    }}
                    onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    Ir a Abrir Caja
                </button>
            </div>
        );
    }

    // =====================================
    // 11. ESTRUCTURA PRINCIPAL CON RESPONSIVIDAD
    // =====================================

    // 576px es el breakpoint para móviles, de modo que las tablets usen la vista de escritorio
    const isSmallScreen = screenWidth <= 576;

    return (
        <>
            {/* Vista responsiva basada en el tamaño de pantalla */}
            {isSmallScreen ? renderCompactView() : renderDesktopView()}

            {/* Aviso: modo agregar productos a una orden existente */}
            {editOrder && (
                <div style={{
                    position: 'fixed',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 950,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '8px 10px 8px 16px',
                    maxWidth: 'calc(100% - 20px)',
                    borderRadius: '999px',
                    backgroundColor: '#c9973a',
                    color: '#2b211b',
                    fontWeight: '700',
                    fontSize: '0.875rem',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                }}>
                    <i className="bi bi-pencil-square"></i>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {loadingEditOrder ? 'Cargando orden...' : `Editando orden${editOrder.order_sequence ? ` #${editOrder.order_sequence}` : ''}${editOrder.table_label ? ` • ${editOrder.table_label}` : ''}`}
                    </span>
                    <button
                        onClick={cancelEditOrder}
                        style={{ border: 'none', borderRadius: '999px', padding: '4px 12px', backgroundColor: '#2b211b', color: '#ffffff', fontWeight: '700', fontSize: '0.8rem', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                        Cancelar
                    </button>
                </div>
            )}

            {/* Modal para agregar nota */}
            {editingNoteForItem && renderNoteModal()}

            {/* Modal Confirmación (Compartido) */}
            {showReviewModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    padding: isSmallScreen ? '0.5rem' : '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                        width: '100%',
                        maxWidth: isSmallScreen ? '95%' : '550px',
                        maxHeight: '90vh',
                        overflowY: 'auto',
                        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                    }}>
                        <div style={{
                            padding: isSmallScreen ? '1rem' : '1.5rem',
                            borderBottom: '2px solid #e6dfd3',
                            backgroundColor: 'var(--primary-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                        }}>
                            <div>
                                <h3 style={{
                                    fontSize: isSmallScreen ? '1.25rem' : '1.5rem',
                                    fontWeight: '700',
                                    color: '#ffffff',
                                    margin: 0
                                }}>
                                    {editOrder ? `Editar orden${editOrder.order_sequence ? ` #${editOrder.order_sequence}` : ''}` : 'Confirmación de Pedido'}
                                </h3>
                                <p style={{
                                    color: '#d6ccbc',
                                    fontSize: isSmallScreen ? '0.75rem' : '0.9rem',
                                    margin: '0.25rem 0 0 0'
                                }}>
                                    {editOrder
                                        ? `La orden (${editOrder.table_label || 'sin mesa'}) quedará con estos productos. Los cambios se enviarán a cocina.`
                                        : 'Confirma la orden para enviarla a preparación (Cocina). El cobro se realizará en Órdenes.'}
                                </p>
                            </div>
                        </div>

                        {renderReviewDetails()}

                        <div style={{
                            padding: isSmallScreen ? '1rem' : '1.5rem',
                            borderTop: '2px solid #e6dfd3',
                            display: 'flex',
                            gap: '0.75rem',
                            justifyContent: 'space-between'
                        }}>
                            <button
                                style={{
                                    padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                    backgroundColor: 'var(--secondary-color)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    color: '#ffffff',
                                    fontWeight: '600',
                                    fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    flex: 1,
                                    minHeight: TOUCH_MIN_SIZE
                                }}
                                onClick={() => setShowReviewModal(false)}
                            >
                                Editar Pedido
                            </button>

                            {editOrder && (
                                <button
                                    style={{
                                        padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '700',
                                        fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                        cursor: processingOrder ? 'not-allowed' : 'pointer',
                                        flex: 1.5,
                                        minHeight: TOUCH_MIN_SIZE
                                    }}
                                    onClick={submitOrderEdit}
                                    disabled={processingOrder}
                                >
                                    {processingOrder ? 'Procesando...' : 'Guardar cambios'}
                                </button>
                            )}

                            {!editOrder && currentLocalShift?.id && (
                                <button
                                    style={{
                                        padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                        backgroundColor: 'var(--primary-color)',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '700',
                                        fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                        cursor: processingOrder ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        flex: 1.5,
                                        minHeight: TOUCH_MIN_SIZE,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}
                                    onClick={() => handleInitiatePlaceOrder(false)}
                                    disabled={processingOrder}
                                >
                                    {processingOrder ? 'Procesando...' : 'Confirmar y Preparar'}
                                </button>
                            )}

                            {!editOrder && canAccessFeria && currentFeriaShift?.id && (
                                <button
                                    style={{
                                        padding: isSmallScreen ? '0.75rem' : '0.75rem 1.5rem',
                                        backgroundColor: '#3d5c37',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: '#ffffff',
                                        fontWeight: '700',
                                        fontSize: isSmallScreen ? '0.875rem' : '1rem',
                                        cursor: processingOrder ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.2s',
                                        flex: 1.5,
                                        minHeight: TOUCH_MIN_SIZE,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}
                                    onClick={() => handleInitiatePlaceOrder(true)}
                                    disabled={processingOrder}
                                >
                                    {processingOrder ? 'Procesando...' : 'Preparar (Feria)'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Toppings */}
            {showToppingsModal && activeToppingsItemIndex !== null && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                    display: 'flex', justifyContent: 'center', alignItems: 'center',
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', width: '100%', maxWidth: '400px',
                        padding: '1.5rem', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)',
                        maxHeight: '90vh', overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, color: '#1f1f1f', fontSize: '1.25rem' }}>Seleccionar Toppings</h3>
                            <button 
                                onClick={() => setShowToppingsModal(false)}
                                style={{ background: 'none', border: 'none', fontSize: '1.5rem', color: '#a89c8c', cursor: 'pointer' }}
                            >×</button>
                        </div>
                        
                        <p style={{ color: '#6b5e51', fontSize: '0.875rem', marginBottom: '1rem' }}>
                            Puedes seleccionar hasta {cart[activeToppingsItemIndex].max_toppings} toppings.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            {availableToppings.map(topping => {
                                const activeItem = cart[activeToppingsItemIndex];
                                const count = activeItem.selectedToppings?.filter(t => t === topping.name).length || 0;
                                const canSelectMore = (activeItem.selectedToppings?.length || 0) < activeItem.max_toppings;
                                
                                return (
                                    <div 
                                        key={topping.id}
                                        style={{
                                            padding: '0.75rem 1rem',
                                            borderRadius: '8px',
                                            border: `1px solid #e6dfd3`,
                                            backgroundColor: count > 0 ? '#fcf3f0' : '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <span style={{ fontWeight: count > 0 ? 'bold' : 'normal', color: '#1f1f1f' }}>
                                            {topping.name}
                                        </span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <button
                                                onClick={() => {
                                                    if (count > 0) {
                                                        const newCart = [...cart];
                                                        const currentToppings = newCart[activeToppingsItemIndex].selectedToppings || [];
                                                        const indexToRemove = currentToppings.lastIndexOf(topping.name);
                                                        if (indexToRemove > -1) {
                                                            currentToppings.splice(indexToRemove, 1);
                                                        }
                                                        newCart[activeToppingsItemIndex].selectedToppings = currentToppings;
                                                        setCart(newCart);
                                                    }
                                                }}
                                                style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #d6ccbc', background: '#fff', cursor: count > 0 ? 'pointer' : 'not-allowed', opacity: count > 0 ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >-</button>
                                            <span style={{ minWidth: '1.5rem', textAlign: 'center', fontWeight: '600' }}>{count}</span>
                                            <button
                                                onClick={() => {
                                                    if (canSelectMore) {
                                                        const newCart = [...cart];
                                                        const currentToppings = newCart[activeToppingsItemIndex].selectedToppings || [];
                                                        currentToppings.push(topping.name);
                                                        newCart[activeToppingsItemIndex].selectedToppings = currentToppings;
                                                        setCart(newCart);
                                                    } else {
                                                        alert(`Límite alcanzado: máximo ${activeItem.max_toppings} toppings.`);
                                                    }
                                                }}
                                                style={{ width: '28px', height: '28px', borderRadius: '4px', border: '1px solid #d6ccbc', background: '#fff', cursor: canSelectMore ? 'pointer' : 'not-allowed', opacity: canSelectMore ? 1 : 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}
                                            >+</button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                            <button 
                                onClick={() => setShowToppingsModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: 'var(--primary-color)',
                                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Listo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Variables */}
            {showVariablesModal && selectedProductForVariables && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '12px', padding: '2rem',
                        width: '90%', maxWidth: '500px', maxHeight: '90vh', overflowY: 'auto',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <h3 style={{ margin: 0, color: '#1f1f1f' }}>Personalizar {selectedProductForVariables.product.name}</h3>
                            <button onClick={() => setShowVariablesModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#8a7d70' }}>&times;</button>
                        </div>
                        
                        {selectedProductForVariables.product.variables.map(variable => (
                            <div key={variable.id} style={{ marginBottom: '1.5rem' }}>
                                <h4 style={{ margin: '0 0 0.5rem 0', color: '#4a3f36' }}>{variable.name} <span style={{ color: '#c0574a' }}>*</span></h4>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.5rem' }}>
                                    {variable.options?.filter(o => o.is_active).map(option => {
                                        const isSelected = currentVariablesSelection[variable.id]?.id === option.id;
                                        return (
                                            <div 
                                                key={option.id}
                                                onClick={() => setCurrentVariablesSelection(prev => ({ ...prev, [variable.id]: option }))}
                                                style={{
                                                    padding: '0.75rem',
                                                    border: `2px solid ${isSelected ? 'var(--primary-color)' : '#e6dfd3'}`,
                                                    borderRadius: '8px',
                                                    backgroundColor: isSelected ? '#f7f1e7' : '#fff',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    fontWeight: isSelected ? 'bold' : 'normal',
                                                    color: isSelected ? 'var(--primary-color)' : '#4a3f36'
                                                }}
                                            >
                                                <div>{option.name}</div>
                                                {parseFloat(option.extra_price) > 0 && (
                                                    <div style={{ fontSize: '0.75rem', marginTop: '0.25rem', color: isSelected ? 'var(--primary-color)' : '#8a7d70' }}>
                                                        +${parseFloat(option.extra_price).toFixed(2)}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}

                        <div style={{ marginTop: '2rem', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button 
                                onClick={() => setShowVariablesModal(false)}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: '#e6dfd3',
                                    color: '#4a3f36', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmVariablesSelection}
                                style={{
                                    padding: '0.75rem 1.5rem', backgroundColor: 'var(--primary-color)',
                                    color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                                }}
                            >
                                Confirmar y Agregar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Órdenes en Espera (Held / Parked Orders) */}
            {showHeldOrdersModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1100,
                    padding: '1rem'
                }}>
                    <div style={{
                        backgroundColor: '#fff', borderRadius: '14px', width: '100%',
                        maxWidth: '650px', maxHeight: '85vh', overflow: 'hidden',
                        display: 'flex', flexDirection: 'column', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
                    }}>
                        {/* Header Modal */}
                        <div style={{
                            padding: '1.25rem 1.5rem', backgroundColor: '#f7ecd3',
                            borderBottom: '2px solid #efdca8', display: 'flex',
                            justifyContent: 'space-between', alignItems: 'center'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                                <i className="bi bi-pause-circle-fill" style={{ fontSize: '1.5rem', color: '#b07f2b' }}></i>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#6f4d17', fontWeight: '700' }}>
                                        Órdenes en Espera
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '0.8rem', color: '#8f6520' }}>
                                        Selecciona una orden para recuperarla y continuar con el cobro
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowHeldOrdersModal(false)}
                                style={{
                                    background: 'none', border: 'none', fontSize: '1.5rem',
                                    cursor: 'pointer', color: '#6f4d17'
                                }}
                            >
                                &times;
                            </button>
                        </div>

                        {/* Contenido Modal */}
                        <div style={{ padding: '1.25rem 1.5rem', overflowY: 'auto', flex: 1 }}>
                            {heldOrders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#8a7d70' }}>
                                    <i className="bi bi-inbox" style={{ fontSize: '3rem', color: '#d6ccbc', display: 'block', marginBottom: '0.5rem' }}></i>
                                    <h4 style={{ margin: '0 0 0.25rem 0', color: '#4a3f36' }}>No hay órdenes en espera</h4>
                                    <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                        Cuando un cliente tarde en decidir o pagar, presiona "En Espera" en el carrito para guardarlo aquí y atender al siguiente.
                                    </p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {heldOrders.map((ho) => {
                                        const customerName = ho.selectedCustomer ? `${ho.selectedCustomer.first_name || ''} ${ho.selectedCustomer.last_name || ''}`.trim() : (ho.customerSearch || 'Consumidor Final');
                                        const itemCount = ho.cart ? ho.cart.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
                                        return (
                                            <div
                                                key={ho.id}
                                                style={{
                                                    border: '2px solid #e6dfd3',
                                                    borderRadius: '10px',
                                                    padding: '1rem',
                                                    backgroundColor: '#faf7f1',
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    gap: '1rem',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                                        <span style={{
                                                            backgroundColor: '#f0e6d6', color: '#3a2618',
                                                            padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700'
                                                        }}>
                                                            {ho.id}
                                                        </span>
                                                        <span style={{ fontSize: '0.8rem', color: '#8a7d70', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                            <i className="bi bi-clock"></i> Guardada: {ho.held_at}
                                                        </span>
                                                        {ho.selectedTable && (
                                                            <span style={{ fontSize: '0.8rem', color: '#46693f', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                                <i className="bi bi-grid-3x3-gap-fill"></i> Mesa: {ho.selectedTable}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontWeight: '700', color: '#2b211b', fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <i className="bi bi-person-fill"></i> {customerName}
                                                    </div>
                                                    <div style={{ fontSize: '0.85rem', color: '#6b5e51', marginTop: '0.25rem' }}>
                                                        {itemCount} producto{itemCount > 1 ? 's' : ''}: {ho.cart?.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                                                    </div>
                                                    <div style={{ fontSize: '1rem', fontWeight: '800', color: 'var(--primary-color)', marginTop: '0.35rem' }}>
                                                        Total: {ho.currency === 'COP' ? `COP ${(ho.total * exchangeRate).toLocaleString('es-CO')}` : formatCurrency(ho.total)}
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRestoreHeldOrder(ho)}
                                                        style={{
                                                            padding: '0.6rem 1rem',
                                                            backgroundColor: 'var(--primary-color)',
                                                            color: '#fff',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            fontWeight: '700',
                                                            fontSize: '0.875rem',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        <i className="bi bi-play-fill"></i> Recuperar
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleDeleteHeldOrder(ho.id, e)}
                                                        style={{
                                                            padding: '0.6rem 0.75rem',
                                                            backgroundColor: '#f7e5e0',
                                                            color: '#96372c',
                                                            border: '1px solid #e6ad9f',
                                                            borderRadius: '8px',
                                                            fontWeight: '600',
                                                            cursor: 'pointer'
                                                        }}
                                                        title="Eliminar orden en espera"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Footer Modal */}
                        <div style={{
                            padding: '1rem 1.5rem', backgroundColor: '#faf7f1',
                            borderTop: '1px solid #e6dfd3', display: 'flex',
                            justifyContent: 'flex-end'
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowHeldOrdersModal(false)}
                                style={{
                                    padding: '0.6rem 1.25rem', backgroundColor: '#e6dfd3',
                                    color: '#4a3f36', border: 'none', borderRadius: '8px',
                                    fontWeight: '600', cursor: 'pointer'
                                }}
                            >
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Overlay animado de carga durante facturación e impresión */}
            {renderPrintOverlay()}

            {/* Modal de Configuración de Secuencia y Reinicio Diario */}
            <ConfiguracionSecuenciaModal
                isOpen={showSequenceModal}
                onClose={() => setShowSequenceModal(false)}
            />
        </>
    );
};

export default PuntosVenta;